import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, ImagePlus, Loader2, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KIND_LABEL = { welcome: "Welcome", update: "What's new", bugfix: "Bug fixed", broadcast: "Announcement", reply: "Reply from our team" };

// Shared "Send message to us" form (text + optional screenshot).
export const SupportForm = ({ popupId, onDone }) => {
  const [msg, setMsg] = useState("");
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try { setImg(await api.uploadSupportImage(f)); } catch (e) { toast.error(e?.response?.data?.detail || "Image upload failed"); }
    setBusy(false);
  };
  const send = async () => {
    if (!msg.trim()) { toast.error("Please write a message"); return; }
    setBusy(true);
    try { await api.sendSupport({ message: msg, popup_id: popupId, image_path: img?.path, page: window.location.pathname }); toast.success("Message sent — we'll get back to you here"); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid="support-form">
      <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} placeholder="Tell us what's wrong or what you need…" data-testid="support-message-input"
        className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF]/50" />
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="support-image-input" />
        {img ? (
          <span className="relative"><img src={mediaUrl(img.path)} alt="attachment" className="h-12 w-12 rounded-lg object-cover" data-testid="support-image-preview" />
            <button onClick={() => setImg(null)} className="absolute -right-1.5 -top-1.5 rounded-full bg-black p-0.5 text-white"><X size={10} /></button></span>
        ) : (
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="support-image-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs text-[#A1A1AA] hover:text-white"><ImagePlus size={13} /> Add screenshot</button>
        )}
        <button onClick={send} disabled={busy} data-testid="support-send-btn" className="ml-auto flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-4 py-1.5 text-xs font-bold text-black disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send</button>
      </div>
    </div>
  );
};

// Live pop-ups for logged-in users: welcome / updates / bug fixes / broadcasts / personal replies.
export default function PopupCenter() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [contact, setContact] = useState(false);
  const shownRef = useRef(new Set());
  useEffect(() => {
    if (!user) { setQueue([]); return undefined; }
    const poll = () => api.activePopups().then((rows) => {
      const fresh = (rows || []).filter((r) => !shownRef.current.has(r.id));
      if (fresh.length) { fresh.forEach((r) => shownRef.current.add(r.id)); setQueue((q) => [...q, ...fresh]); }
    }).catch(() => {});
    poll();
    const iv = setInterval(poll, 45000);
    window.addEventListener("focus", poll);
    return () => { clearInterval(iv); window.removeEventListener("focus", poll); };
  }, [user]);
  const cur = queue[0];
  const close = () => { if (cur) api.dismissPopup(cur.id).catch(() => {}); setContact(false); setQueue((q) => q.slice(1)); };
  if (!cur) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent noSearch className="max-w-md overflow-hidden border-white/10 bg-[#121212] p-0" data-testid="site-popup">
        {cur.image_path && <img src={mediaUrl(cur.image_path)} alt="" className="max-h-64 w-full object-cover" data-testid="site-popup-image" />}
        <div className="space-y-3 p-5">
          {cur.kind !== "reply" && <span className="text-[10px] font-bold uppercase tracking-widest text-[#00F0FF]">{KIND_LABEL[cur.kind] || "Message"}</span>}
          {cur.title && <DialogTitle className="font-display text-lg font-black text-white" data-testid="site-popup-title">{cur.title}</DialogTitle>}
          {!cur.title && <DialogTitle className="sr-only">Message</DialogTitle>}
          {cur.message && <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#D4D4D8]" data-testid="site-popup-message">{cur.message}</p>}
          {contact ? <SupportForm popupId={cur.id} onDone={close} /> : (
            <div className="flex items-center gap-2 pt-1">
              {cur.allow_contact !== false && <button onClick={() => setContact(true)} data-testid="site-popup-contact" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-[#A1A1AA] hover:text-white"><MessageCircle size={14} /> Send message to us</button>}
              <button onClick={close} data-testid="site-popup-ok" className="ml-auto rounded-lg bg-[#00F0FF] px-6 py-2 text-sm font-bold text-black hover:bg-[#00F0FF]/90">{cur.button_label || "OK"}</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
